import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, Timer, ChartLine, Sparkle, Star, Quotes, GraduationCap, MapPin, Phone, GlobeHemisphereEast, WhatsappLogo } from "@phosphor-icons/react";

const WA_MSG_CALCULUS = encodeURIComponent(
    "Hello Mr. Calculus 👋 — I'd like to book a session at Wibpro Academy. My target exam is:"
);
const WA_LINK_CALCULUS = `https://wa.me/919100560075?text=${WA_MSG_CALCULUS}`;
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const FEATURES = [
    { icon: BookOpen, title: "13+ Exam Tracks", body: "EAPCET · NEET · GRE · GMAT · IELTS · SAT · PTE · UPSC · CAT · SSC · RRB · IBPS · Banking." },
    { icon: Sparkle, title: "AI-Crafted Questions", body: "Fresh practice sets every time, tuned to your target difficulty and weak topics." },
    { icon: Timer, title: "Timed Mock Simulations", body: "Real exam pacing with question-navigator, mark-for-review, and time-per-question analytics." },
    { icon: ChartLine, title: "Mistake Analysis", body: "See exactly which topics dragged you down, with a personalised 7-day study plan." },
];

const COURSES_TAGS = [
    { name: "EAPCET", tone: "bg-ink text-bone" },
    { name: "NEET", tone: "bg-[#065F46] text-bone" },
    { name: "GRE", tone: "bg-[#1E3A8A] text-bone" },
    { name: "GMAT", tone: "bg-[#7C2D12] text-bone" },
    { name: "UPSC", tone: "bg-oxblood text-bone" },
    { name: "CAT", tone: "bg-[#365314] text-bone" },
    { name: "IELTS", tone: "bg-[#B45309] text-bone" },
    { name: "SAT", tone: "bg-[#831843] text-bone" },
    { name: "PTE", tone: "bg-[#134E4A] text-bone" },
    { name: "IBPS", tone: "bg-[#1E40AF] text-bone" },
    { name: "SSC CGL", tone: "bg-[#5B21B6] text-bone" },
    { name: "RRB", tone: "bg-[#0F766E] text-bone" },
];

export default function LandingPage() {
    return (
        <div className="min-h-screen paper-grain" data-testid="landing-page">
            <Navbar />

            {/* Hero */}
            <section className="mx-auto max-w-7xl px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-12 items-end">
                <div className="lg:col-span-7 animate-fade-up">
                    <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-ink/60 mb-6">
                        <span className="w-8 h-px bg-gold"></span>
                        <span>An institute of applied preparation</span>
                    </div>
                    <h1 className="font-serif font-black text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] text-ink">
                        Master the exam.
                        <br />
                        <span className="italic font-light text-ink/80">Not just the syllabus.</span>
                    </h1>
                    <p className="mt-8 text-lg md:text-xl text-ink/70 max-w-xl leading-relaxed">
                        Wibpro Academy pairs classical, rigorous study material with an AI coach that
                        finds — and fixes — the exact gaps holding your score back.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <Link to="/register">
                            <Button size="lg" className="bg-ink text-bone hover:bg-ink/90 rounded-sm px-8 h-12 group" data-testid="btn-hero-register">
                                Begin your preparation
                                <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                            </Button>
                        </Link>
                        <Link to="/courses" className="text-sm underline underline-offset-4 decoration-gold decoration-2 text-ink hover:text-gold transition-colors" data-testid="btn-hero-browse">
                            Browse the catalog →
                        </Link>
                    </div>

                    <div className="mt-14 flex items-center gap-8">
                        <div>
                            <div className="font-serif text-3xl text-ink">13+</div>
                            <div className="text-xs uppercase tracking-widest text-ink/50">Exam tracks</div>
                        </div>
                        <div className="w-px h-10 bg-ink/15"></div>
                        <div>
                            <div className="font-serif text-3xl text-ink">∞</div>
                            <div className="text-xs uppercase tracking-widest text-ink/50">Practice questions</div>
                        </div>
                        <div className="w-px h-10 bg-ink/15"></div>
                        <div>
                            <div className="font-serif text-3xl text-ink">1:1</div>
                            <div className="text-xs uppercase tracking-widest text-ink/50">AI feedback</div>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
                    <div className="relative aspect-[4/5] overflow-hidden rounded-sm border-2 border-ink/10 shadow-2xl">
                        <img
                            src="https://images.unsplash.com/photo-1517971129774-8a2b38fa128e?crop=entropy&cs=srgb&fm=jpg&q=85"
                            alt="Focused student"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent"></div>
                        <div className="absolute bottom-6 left-6 right-6 text-bone">
                            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-2">Chapter I</div>
                            <div className="font-serif text-2xl leading-tight">The examined life<br/>begins with an examined mind.</div>
                        </div>
                    </div>
                    <div className="absolute -bottom-6 -left-6 bg-bone border border-ink/15 p-4 rounded-sm shadow-lg hidden md:block max-w-[260px]">
                        <div className="flex items-center gap-1 text-gold mb-1">
                            {[1,2,3,4,5].map(i => <Star key={i} size={12} weight="fill" />)}
                        </div>
                        <div className="text-xs text-ink leading-snug">"Cleared IBPS in 3 months. The mistake analysis is a game-changer."</div>
                        <div className="text-[10px] text-ink/60 mt-1 tracking-wide uppercase">— A. Rao, Wibpro cohort '24</div>
                    </div>
                </div>
            </section>

            {/* Courses strip */}
            <section className="border-y border-ink/10 bg-parchment/40">
                <div className="mx-auto max-w-7xl px-6 py-12">
                    <div className="text-xs uppercase tracking-[0.3em] text-ink/50 mb-6">One platform · every serious exam</div>
                    <div className="flex flex-wrap gap-3">
                        {COURSES_TAGS.map(c => (
                            <span key={c.name} className={`px-4 py-2 text-sm font-medium rounded-sm ${c.tone}`} data-testid={`course-tag-${c.name.toLowerCase().replace(/\s+/g,'-')}`}>
                                {c.name}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features (bento) */}
            <section className="mx-auto max-w-7xl px-6 py-24">
                <div className="grid md:grid-cols-12 gap-8 mb-14">
                    <div className="md:col-span-5">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">The Wibpro Method</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight gold-underline inline">
                            Rigor, meet <em className="font-light">precision.</em>
                        </h2>
                    </div>
                    <p className="md:col-span-7 text-ink/70 text-lg leading-relaxed md:mt-6">
                        We built Wibpro for the student who is tired of generic mock tests and wants to know,
                        with mathematical certainty, exactly what to fix before exam day.
                    </p>
                </div>

                <div className="grid md:grid-cols-4 gap-6">
                    {FEATURES.map((f, i) => (
                        <div key={f.title} className="p-8 border border-ink/10 bg-bone hover:border-ink/30 hover:-translate-y-1 transition-[transform,border-color] duration-200 rounded-sm" data-testid={`feature-${i}`}>
                            <f.icon size={32} weight="duotone" className="text-ink mb-6" />
                            <div className="font-serif text-xl text-ink mb-2">{f.title}</div>
                            <div className="text-sm text-ink/70 leading-relaxed">{f.body}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Method / editorial section */}
            <section className="bg-ink text-bone py-24">
                <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-5">
                        <img
                            src="https://images.pexels.com/photos/34260079/pexels-photo-34260079.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"
                            alt="Library"
                            className="w-full aspect-[4/5] object-cover rounded-sm"
                        />
                    </div>
                    <div className="lg:col-span-7">
                        <Quotes size={40} weight="duotone" className="text-gold mb-6" />
                        <h3 className="font-serif text-3xl md:text-4xl leading-tight">
                            "You do not rise to the level of your goals.
                            You fall to the level of your systems."
                        </h3>
                        <p className="mt-8 text-bone/70 leading-relaxed max-w-2xl">
                            Every question you attempt on Wibpro is logged. Every second is timed. Every mistake is
                            categorised by topic and by cause. Then, an AI coach — powered by Claude Sonnet and Gemini —
                            writes you a study plan that reads like it came from a private tutor who has spent decades
                            watching students like you.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-8 text-sm">
                            <div>
                                <div className="font-serif text-2xl">Study material</div>
                                <div className="text-bone/60">Curated by topic</div>
                            </div>
                            <div className="w-px bg-bone/20"></div>
                            <div>
                                <div className="font-serif text-2xl">Practice tests</div>
                                <div className="text-bone/60">Adaptive difficulty</div>
                            </div>
                            <div className="w-px bg-bone/20"></div>
                            <div>
                                <div className="font-serif text-2xl">Full mocks</div>
                                <div className="text-bone/60">Exam-day simulation</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Faculty spotlight */}
            <section className="mx-auto max-w-7xl px-6 py-24">
                <div className="grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <div className="relative aspect-square overflow-hidden rounded-sm border-2 border-ink/10 bg-ink">
                            <div className="absolute inset-0 grid place-items-center">
                                <div className="text-center">
                                    <div className="w-40 h-40 mx-auto rounded-full bg-gradient-to-br from-gold to-gold-dark grid place-items-center text-ink font-serif text-6xl">
                                        AF
                                    </div>
                                    <div className="mt-8 text-bone font-serif text-4xl">Abul Faisal</div>
                                    <div className="mt-2 text-gold font-serif italic text-xl">Mr. Calculus</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">The Faculty</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight">
                            Learn from the teacher students <em className="font-light">actually name.</em>
                        </h2>
                        <p className="mt-6 text-lg text-ink/70 leading-relaxed max-w-2xl">
                            At Wibpro Academy, mathematics isn't a subject — it's a personality. Meet
                            <span className="font-serif italic text-ink"> Abul Faisal</span>, known across Hyderabad's
                            engineering-aspirant circles simply as
                            <span className="font-serif italic text-ink"> Mr. Calculus</span>. His students don't just
                            solve problems; they see them for what they are — patterns waiting to be recognised.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <div className="px-4 py-2 border border-ink/15 rounded-sm text-sm text-ink">EAPCET Mathematics</div>
                            <div className="px-4 py-2 border border-ink/15 rounded-sm text-sm text-ink">JEE-level Calculus</div>
                            <div className="px-4 py-2 border border-ink/15 rounded-sm text-sm text-ink">Foundation for CAT/GMAT Quant</div>
                        </div>

                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <a href={WA_LINK_CALCULUS} target="_blank" rel="noopener noreferrer">
                                <Button size="lg" className="bg-[#25D366] text-white hover:bg-[#128C7E] rounded-sm h-12 px-6 group" data-testid="btn-whatsapp-calculus">
                                    <WhatsappLogo size={20} weight="fill" className="mr-2"/>
                                    Book a session with Mr. Calculus
                                </Button>
                            </a>
                            <a href="tel:+919100560075" className="text-sm text-ink/70 hover:text-ink underline underline-offset-4 decoration-gold decoration-2" data-testid="link-call-faculty">
                                or call the academy directly →
                            </a>
                        </div>
                        <div className="mt-2 text-xs text-ink/50">
                            Opens WhatsApp · usually replies within an hour on working days.
                        </div>
                    </div>
                </div>
            </section>

            {/* School Tuitions teaser */}
            <section className="bg-bone border-y border-ink/10">
                <div className="mx-auto max-w-7xl px-6 py-16 grid md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-8">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-3">
                            <BookOpen size={14} weight="duotone"/> School tuitions · Grade 9-12
                        </div>
                        <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight">
                            AS & A Level · IGCSE · CBSE · SSC — all subjects, six languages.
                        </h3>
                        <p className="mt-3 text-ink/70 max-w-2xl">
                            In-person and online tuitions from Hyderabad, taught by faculty who have set the papers themselves.
                            English, Arabic, French, German, Hindi, Telugu — all supported.
                        </p>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link to="/tuitions">
                            <Button size="lg" className="bg-ink text-bone hover:bg-ink/90 rounded-sm px-6 h-12" data-testid="btn-landing-tuitions">
                                Browse tuitions <ArrowRight size={16} className="ml-2"/>
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Papers teaser */}
            <section className="bg-parchment/60 border-t border-ink/10">
                <div className="mx-auto max-w-7xl px-6 py-16 grid md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-8">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-3">
                            <BookOpen size={14} weight="duotone"/> Free library
                        </div>
                        <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight">
                            Past papers, sample papers &amp; topic-wise sets. Free.
                        </h3>
                        <p className="mt-3 text-ink/70 max-w-2xl">
                            IGCSE and AS/A Level papers by subject code &mdash; Mathematics 0580 &amp; 9709,
                            Physics 0625 &amp; 9702, Accounting 0452 &amp; 9706 and more. No signup required.
                        </p>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link to="/resources">
                            <Button size="lg" className="bg-ink text-bone hover:bg-ink/90 rounded-sm px-6 h-12" data-testid="btn-landing-resources">
                                Browse papers <ArrowRight size={16} className="ml-2"/>
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Study Abroad teaser */}
            <section className="bg-parchment/60 border-y border-ink/10">
                <div className="mx-auto max-w-7xl px-6 py-16 grid md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-8">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-3">
                            <GlobeHemisphereEast size={14} weight="duotone"/> Beyond the exam
                        </div>
                        <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight">
                            Planning to study abroad? We do that too.
                        </h3>
                        <p className="mt-3 text-ink/70 max-w-2xl">
                            Overseas education counselling and end-to-end visa processing for USA, UK, Canada, Australia,
                            New Zealand, UAE and the European Union.
                        </p>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link to="/study-abroad">
                            <Button size="lg" className="bg-ink text-bone hover:bg-ink/90 rounded-sm px-6 h-12" data-testid="btn-landing-study-abroad">
                                Explore study abroad <ArrowRight size={16} className="ml-2"/>
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Visit us */}
            <section className="mx-auto max-w-7xl px-6 py-20 grid md:grid-cols-2 gap-10 items-start" id="visit">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Come see us</div>
                    <h3 className="font-serif text-4xl text-ink">Wibpro Academy · Hyderabad</h3>
                    <p className="mt-4 text-ink/70 leading-relaxed">
                        Not a call-center-with-a-course-page. An actual campus with actual teachers.
                        Drop by, sit in on a class, or call us with your syllabus questions.
                    </p>
                </div>
                <div className="border border-ink/15 rounded-sm p-8 bg-bone">
                    <div className="flex items-start gap-3 mb-6" data-testid="landing-address">
                        <MapPin size={22} weight="fill" className="text-gold mt-1 shrink-0"/>
                        <div>
                            <div className="font-serif text-lg text-ink">Head Office</div>
                            <div className="text-sm text-ink/70 leading-relaxed">
                                2nd floor, Safa Arcade,<br/>
                                Opposite pillar 15, Mehdipatnam,<br/>
                                Hyderabad, Telangana, India
                            </div>
                        </div>
                    </div>
                    <div className="flex items-start gap-3" data-testid="landing-phone">
                        <Phone size={22} weight="fill" className="text-gold mt-1 shrink-0"/>
                        <div>
                            <a href="tel:+919100560075" className="font-serif text-lg text-ink hover:text-gold">+91 91005 60075</a>
                            <div className="text-sm text-ink/60">Mon–Sat · 10 AM to 7 PM IST</div>
                        </div>
                    </div>
                    <a href="tel:+919100560075">
                        <Button className="mt-8 w-full bg-ink text-bone hover:bg-ink/90 rounded-sm h-11" data-testid="btn-call-now">
                            <Phone size={16} className="mr-2" weight="fill"/> Call now
                        </Button>
                    </a>
                </div>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-4xl px-6 py-24 text-center">
                <GraduationCap size={48} weight="duotone" className="mx-auto text-gold mb-6" />
                <h3 className="font-serif text-4xl md:text-5xl text-ink leading-tight">
                    Your exam is closer than you think.
                </h3>
                <p className="mt-4 text-ink/70">Register free. Start your first practice test in under a minute.</p>
                <Link to="/register">
                    <Button size="lg" className="mt-8 bg-ink text-bone hover:bg-ink/90 rounded-sm h-12 px-10" data-testid="btn-cta-register">
                        Enrol at Wibpro
                    </Button>
                </Link>
            </section>

            <Footer />
        </div>
    );
}
